import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import {
  ArrowRight,
  Search,
  MapPin,
  CreditCard,
  Utensils,
  Plane,
  ChevronRight,
  MessageCircle,
  Sun,
  CloudRain,
  Bot,
  Sparkles,
  Navigation,
  Mail,
  Calendar,
  Users,
  Wallet,
  Check,
  Shield,
  Eye,
  Zap,
} from "lucide-react";

/* ─── animation variants ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

/* ─── constants ─── */
const WHATSAPP_LINK = "https://wa.me/919582819710?text=Hey%20Shadow%2C%20plan%20me%20a%20sidequest";
const HERO_MESSAGE = "Goa, 3 nights, under 20K, beach vibes, veg";

const VIBE_TAGS = [
  "Weekend trip to Goa",
  "Valentine's dinner in Mumbai",
  "Sunday brunch with the squad",
  "Manali escape in May",
  "After-work rooftop tonight",
  "Birthday dinner, surprise setup",
  "Solo trip to Hampi",
  "Day trip to Lonavala",
];

const STEPS = [
  {
    num: "01",
    title: "Tell us the vibe.",
    desc: "One message on WhatsApp. 'Goa, 3 nights, under 20K' or 'Valentine's dinner, romantic, Mumbai, tonight'. No forms. No apps.",
    icon: MessageCircle,
    tag: "Any occasion",
  },
  {
    num: "02",
    title: "We discover everything.",
    desc: "Reels, reviews, live availability, weather, crowd data, current prices — analysed in seconds. The spots locals know, not just page 1 of Google.",
    icon: Search,
    tag: "Seconds",
  },
  {
    num: "03",
    title: "It gets booked for you.",
    desc: "Restaurant table, hotel, flights, cab, flowers at the table — confirmed. Cancellations handled. Modifications done. You just say yes.",
    icon: CreditCard,
    tag: "Minutes",
  },
  {
    num: "04",
    title: "Enjoy. We handle the rest.",
    desc: "Flight delayed? Cab rebooked. Rain tomorrow? Indoor plan ready. Running late? Your reservation knows. You never have to think about it.",
    icon: Navigation,
    tag: "Always on",
  },
];

const MOMENT_CARDS = [
  {
    emoji: "💕",
    title: "Valentine's dinner sorted",
    body: "Farzi Cafe, Cyber Hub — rooftop table, 8 PM. Flowers on the table arranged with the kitchen. Cab booked for 7:45.",
    time: "Today, 11:30 AM",
    tag: "Date night",
    accent: "bg-rose-50 border-rose-100",
  },
  {
    emoji: "✈️",
    title: "Price drop detected",
    body: "DEL → GOI dropped ₹400. Rebooked at ₹2,800. You saved without lifting a finger.",
    time: "2 days before trip",
    tag: "Weekend trip",
    accent: "bg-sky-50 border-sky-100",
  },
  {
    emoji: "🎂",
    title: "Birthday dinner confirmed",
    body: "Smoke House Deli — private dining room, 8 PM. Told them it's a birthday. Surprise dessert arranged. Squad notified.",
    time: "Saturday, noon",
    tag: "Occasion",
    accent: "bg-violet-50 border-violet-100",
  },
  {
    emoji: "🍽️",
    title: "Sunday brunch sorted",
    body: "The Grammar Room, 11:30 AM. Window seat, outdoor terrace. 4.7★. 10 min from you. Booked.",
    time: "Today, 9:45 AM",
    tag: "Local",
    accent: "bg-teal-50 border-teal-100",
  },
];

const TESTIMONIALS = [
  {
    name: "Priya", age: 26, city: "Bangalore", avatar: "P",
    role: "Solo weekend warrior",
    quote: "Finally something that tells me if an area is safe at night. Planned my entire Hampi solo trip in 10 minutes. It even mapped clean washroom stops on my road trip.",
    color: "bg-violet-100 text-violet-700",
    trip: "Hampi, 3 days, ₹9,200",
  },
  {
    name: "Rohan", age: 27, city: "Delhi", avatar: "R",
    role: "Product designer",
    quote: "Told Shadow 'Valentine's dinner, romantic, under ₹4K, South Delhi'. Got 3 options with why each one fit. Booked the rooftop one, flowers at the table. She had no idea I used an app.",
    color: "bg-teal-100 text-teal-700",
    trip: "Valentine's dinner, Farzi Cafe — ₹3,800",
  },
  {
    name: "Sneha & friends", age: 24, city: "Delhi", avatar: "S",
    role: "Group trip organiser",
    quote: "Bachelorette in Pondicherry. 8 girls. Budget voting on WhatsApp, shared itinerary, UPI split tracked automatically. Zero fights. Best trip we've ever planned.",
    color: "bg-rose-100 text-rose-700",
    trip: "Pondicherry, 3 days, ₹11,400 per head",
  },
];

const AGENTS = [
  {
    name: "Scout",
    role: "Discovery",
    icon: Search,
    color: "text-violet-400",
    glow: "bg-violet-500/10 border-violet-500/20",
    dot: "bg-violet-400",
    desc: "Scans reels, reviews, Reddit, and travel blogs. Surfaces what locals love — not just what ranks on page 1.",
    status: "Analyzed 847 reels",
  },
  {
    name: "Planner",
    role: "Itinerary",
    icon: MapPin,
    color: "text-blue-400",
    glow: "bg-blue-500/10 border-blue-500/20",
    dot: "bg-blue-400",
    desc: "Builds a day-by-day plan around weather forecasts, crowd patterns, permit windows, and your preferences.",
    status: "Day 2 itinerary ready",
  },
  {
    name: "Booker",
    role: "Confirmation",
    icon: CreditCard,
    color: "text-teal-400",
    glow: "bg-teal-500/10 border-teal-500/20",
    dot: "bg-teal-400",
    desc: "Confirms flights, hotels, restaurants, cabs, and activities. Not browsed — booked, with receipts.",
    status: "4 of 4 confirmed",
  },
  {
    name: "Watcher",
    role: "Real-time",
    icon: Eye,
    color: "text-amber-400",
    glow: "bg-amber-500/10 border-amber-500/20",
    dot: "bg-amber-400",
    desc: "Monitors price drops, delays, weather shifts, and sold-out slots. Adapts the plan before you notice anything changed.",
    status: "Flight on time ✓",
  },
  {
    name: "Guard",
    role: "Safety",
    icon: Shield,
    color: "text-emerald-400",
    glow: "bg-emerald-500/10 border-emerald-500/20",
    dot: "bg-emerald-400",
    desc: "Checks neighbourhood safety scores, verifies cabs, and keeps a backup plan ready. Essential for solo travellers.",
    status: "All clear",
  },
];

const LIVE_TRIP_MESSAGES = [
  { from: "agent", agent: "Flight update", text: "Your IndiGo 6E-234 is delayed 2 hrs. I've pushed your cab pickup to 4:30 PM and moved dinner to 9 PM. Your hotel knows you'll be late.", time: "2:15 PM" },
  { from: "user", text: "Damn, thanks! What do I do for 2 hours at the airport?", time: "2:16 PM" },
  { from: "agent", agent: "Quick rec", text: "Terminal 2 has a great cafe called Runway Brew, gate 14. Spa lounge near gate 12 has free entry for IndiGo passengers.", time: "2:16 PM" },
  { from: "agent", agent: "Budget check-in", text: "You've spent ₹12,400 of your ₹20K budget. 2 nights left. You're on track — here's a quick breakdown.", time: "4:45 PM" },
  { from: "agent", agent: "Nearby", text: "You're 15 min from Anjuna Flea Market — it's open till 6 PM. Worth a detour? 3 shops with great reviews for silver jewelry.", time: "5:02 PM" },
  { from: "agent", agent: "Tomorrow", text: "Sunrise at Chapora Fort is at 6:12 AM. Scooter booked for 5:45 AM pickup. UV index is 9 — sunscreen.", time: "9:30 PM" },
];

/* ─── sub-components ─── */
function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });
  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-[2px] bg-[#0a0a0a] z-[60] origin-left"
      style={{ scaleX }}
    />
  );
}

function ChatBubble({ message, index }) {
  const isUser = message.from === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.35 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-2`}
    >
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
        isUser
          ? "bg-[#005c4b] text-white rounded-tr-sm"
          : "bg-white text-gray-800 rounded-tl-sm shadow-sm border border-gray-100"
      }`}>
        {!isUser && message.agent && (
          <p className="text-[10px] font-semibold text-teal-600 mb-0.5 flex items-center gap-1">
            <Bot className="w-3 h-3" />
            {message.agent}
          </p>
        )}
        <p className="text-[13px] leading-relaxed">{message.text}</p>
        <p className={`text-[10px] mt-0.5 text-right ${isUser ? "text-white/60" : "text-gray-400"}`}>{message.time}</p>
      </div>
    </motion.div>
  );
}

/* ─── main page ─── */
export default function SidequestsPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [heroStep, setHeroStep] = useState(0);
  const [typedText, setTypedText] = useState("");
  const [cardLines, setCardLines] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setHeroStep(1), 800);
    return () => clearTimeout(t1);
  }, []);

  useEffect(() => {
    if (heroStep !== 1) return;
    let i = 0;
    const id = setInterval(() => {
      i++;
      if (i > HERO_MESSAGE.length) { clearInterval(id); setTimeout(() => setHeroStep(2), 400); return; }
      setTypedText(HERO_MESSAGE.slice(0, i));
    }, 45);
    return () => clearInterval(id);
  }, [heroStep]);

  useEffect(() => {
    if (heroStep !== 2) return;
    const id = setTimeout(() => setHeroStep(3), 1800);
    return () => clearTimeout(id);
  }, [heroStep]);

  useEffect(() => {
    if (heroStep !== 3) return;
    let line = 0;
    const id = setInterval(() => {
      line++;
      if (line > 5) { clearInterval(id); return; }
      setCardLines(line);
    }, 200);
    return () => clearInterval(id);
  }, [heroStep]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: "smooth" }); setMobileMenuOpen(false); }
  };

  return (
    <div className="min-h-screen bg-white antialiased font-sans">
      <ScrollProgress />

      {/* ════════ NAV ════════ */}
      <header className="fixed top-0 w-full bg-white z-50 border-b border-black/[0.06]">
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[60px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 bg-[#0a0a0a] rounded-full" />
            <span className="text-sm font-bold tracking-tight text-[#0a0a0a]">
              SHADOW<span className="text-teal-600">LABS</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection("how-it-works")} className="text-sm text-gray-500 hover:text-[#0a0a0a] transition-colors">How it works</button>
            <button onClick={() => scrollToSection("proof")} className="text-sm text-gray-500 hover:text-[#0a0a0a] transition-colors">In action</button>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#0a0a0a] text-white px-5 py-2 text-sm rounded-lg hover:bg-[#1a1a1a] transition-colors font-medium flex items-center gap-2"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Start on WhatsApp
            </a>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </nav>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-black/[0.06]">
            <div className="px-5 py-3 space-y-1">
              <button onClick={() => scrollToSection("how-it-works")} className="block w-full text-left py-2 text-sm text-gray-600">How it works</button>
              <button onClick={() => scrollToSection("proof")} className="block w-full text-left py-2 text-sm text-gray-600">In action</button>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="block w-full text-left py-2 text-sm text-teal-600 font-medium">Start on WhatsApp →</a>
            </div>
          </div>
        )}
      </header>

      {/* ════════ HERO ════════ */}
      <section className="relative pt-28 sm:pt-36 pb-20 sm:pb-28 px-5 sm:px-8 bg-[#0a0a0a] min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_0%,rgba(13,148,136,0.12),transparent)]" />

        <div className="max-w-7xl mx-auto relative w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp} custom={0} className="mb-7">
                <span className="inline-flex items-center gap-2 border border-white/10 text-white/50 text-[11px] font-medium px-3 py-1.5 rounded-full tracking-wide">
                  <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse" />
                  AI-powered Travel · India
                </span>
              </motion.div>

              <motion.h1 variants={fadeUp} custom={1} className="text-[3rem] sm:text-[4rem] lg:text-[4.5rem] font-bold tracking-tight leading-[0.96] text-white mb-7">
                Big trips.
                <br />
                Sunday lunches.
                <br />
                <span className="text-teal-400">One window.</span>
              </motion.h1>

              <motion.p variants={fadeUp} custom={2} className="text-base sm:text-lg text-white/40 max-w-lg mb-10 leading-relaxed">
                No tabs. No apps. No spreadsheets. Whether it's a 3-night Goa escape
                or tonight's Valentine's dinner — just tell us the vibe and it's handled,
                start to finish.
              </motion.p>

              <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row items-start gap-3">
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-[#25D366] text-white px-8 py-3.5 text-sm rounded-lg font-medium flex items-center gap-2.5 hover:bg-[#22c55e] transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  Start on WhatsApp
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </a>
                <button
                  onClick={() => scrollToSection("how-it-works")}
                  className="text-white/40 hover:text-white/70 px-6 py-3.5 text-sm font-medium flex items-center gap-1.5 transition-colors"
                >
                  See how it works
                  <ChevronRight className="w-4 h-4" />
                </button>
              </motion.div>

              <motion.div variants={fadeUp} custom={4} className="mt-10">
                <p className="text-[10px] text-white/20 uppercase tracking-[0.15em] font-medium mb-3">Works for any sidequest</p>
                <div className="flex flex-wrap gap-2">
                  {VIBE_TAGS.map((tag) => (
                    <span key={tag} className="text-[11px] text-white/30 border border-white/[0.07] px-2.5 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            </motion.div>

            {/* Auto-typing WhatsApp Chat */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}>
              <div className="bg-[#0b141a] rounded-2xl border border-white/[0.06] overflow-hidden shadow-2xl max-w-sm mx-auto">
                <div className="bg-[#1f2c34] px-4 py-3 flex items-center gap-3 border-b border-white/[0.04]">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Shadow Sidequests</p>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <p className="text-[10px] text-emerald-400">online</p>
                    </div>
                  </div>
                </div>

                <div className="bg-[#0b141a] px-3 py-4 space-y-2.5 min-h-[360px] flex flex-col justify-end">
                  <AnimatePresence>
                    {heroStep >= 1 && (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex justify-end">
                        <div className="bg-[#005c4b] text-white rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[80%]">
                          <p className="text-[13px] leading-relaxed">
                            {typedText}
                            {heroStep === 1 && <span className="inline-block w-0.5 h-4 bg-white/60 ml-0.5 animate-pulse align-middle" />}
                          </p>
                          {heroStep >= 2 && <p className="text-[10px] text-white/50 text-right mt-1">7:32 PM</p>}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {heroStep >= 2 && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-1.5 py-1">
                        {[
                          { label: "Searching", text: "847 reels, 2.3K reviews analyzed...", color: "text-violet-400", delay: 0 },
                          { label: "Checking", text: "weather, crowds, fares for your dates...", color: "text-blue-400", delay: 0.3 },
                          { label: "Booking", text: "3 perfect stays found in budget...", color: "text-emerald-400", delay: 0.6 },
                        ].map((item) => (
                          <motion.div
                            key={item.label}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: item.delay }}
                            className="flex items-center gap-2"
                          >
                            <div className={`w-1.5 h-1.5 rounded-full ${item.color.replace("text-", "bg-")} animate-pulse`} />
                            <p className={`text-[10px] ${item.color}`}>
                              <span className="font-semibold">{item.label}</span> {item.text}
                            </p>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {heroStep >= 3 && (
                      <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 20 }}
                        className="flex justify-start"
                      >
                        <div className="bg-white rounded-2xl rounded-tl-sm shadow-xl max-w-[90%] overflow-hidden">
                          <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-4 py-2">
                            <p className="text-white text-xs font-semibold flex items-center gap-1.5">
                              <Sparkles className="w-3 h-3" />
                              Your Goa Sidequest is ready
                            </p>
                          </div>
                          <div className="px-4 py-3 space-y-2.5">
                            {[
                              { icon: Plane, color: "text-blue-500", text: <><span className="font-semibold">DEL → GOI</span> IndiGo 6E-234 · ₹3,200</> },
                              { icon: MapPin, color: "text-rose-500", text: <><span className="font-semibold">Zostel Calangute</span> · ₹1,800/night · 4.4★</> },
                              { icon: Utensils, color: "text-amber-500", text: <><span className="font-semibold">Artjuna Cafe</span> reserved · 100% veg · 4.6★</> },
                              { icon: Sun, color: "text-yellow-500", text: <>28°C, sunny all 3 days</> },
                            ].map((line, li) => {
                              const LIcon = line.icon;
                              return (
                                <motion.div
                                  key={li}
                                  initial={{ opacity: 0, x: -8 }}
                                  animate={cardLines > li ? { opacity: 1, x: 0 } : {}}
                                  transition={{ duration: 0.3 }}
                                  className="flex items-center gap-2 text-[11px]"
                                >
                                  <LIcon className={`w-3 h-3 ${line.color}`} />
                                  <span className="text-gray-700">{line.text}</span>
                                </motion.div>
                              );
                            })}
                            {cardLines >= 5 && (
                              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-1.5 border-t border-gray-100">
                                <p className="text-[11px] font-semibold text-gray-900">
                                  Total: ₹17,400 <span className="text-emerald-600 font-normal">within budget</span>
                                </p>
                              </motion.div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════ PAIN POINT ════════ */}
      <section className="py-28 sm:py-40 px-5 sm:px-8 bg-[#fafafa] border-b border-black/[0.06] overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-[0.15em] mb-10">The old way</p>
            <div className="space-y-1 mb-12">
              {["12 tabs.", "3 apps.", "45 minutes."].map((line, i) => (
                <motion.p
                  key={line}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="text-[3.5rem] sm:text-[5rem] lg:text-[6.5rem] font-black tracking-tight leading-[0.95]"
                  style={{ color: `rgba(10,10,10,${0.12 + i * 0.1})` }}
                >
                  {line}
                </motion.p>
              ))}
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="text-xl sm:text-2xl text-gray-400 font-normal pt-4"
              >
                For a dinner reservation.
              </motion.p>
            </div>

            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="h-px bg-black/[0.1] mb-12 origin-left"
            />

            <div className="space-y-1">
              <motion.p
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="text-[3.5rem] sm:text-[5rem] lg:text-[6.5rem] font-black tracking-tight leading-[0.95] text-[#0a0a0a]"
              >
                One message.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.55 }}
                className="text-[3.5rem] sm:text-[5rem] lg:text-[6.5rem] font-black tracking-tight leading-[0.95] text-teal-500"
              >
                30 seconds.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════ HOW IT WORKS ════════ */}
      <section id="how-it-works" className="py-24 sm:py-32 px-5 sm:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-[0.15em] mb-5">How it works</p>
            <h2 className="text-3xl sm:text-4xl lg:text-[3.25rem] font-bold tracking-tight text-[#0a0a0a] leading-[1.08] max-w-xl">
              From one message to a perfect trip.
            </h2>
          </div>

          <div className="divide-y divide-black/[0.06]">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.num}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="py-12 sm:py-14 flex flex-col sm:flex-row sm:items-start gap-6 sm:gap-16 group"
                >
                  <span className="text-[5rem] sm:text-[7rem] font-black text-black/[0.06] leading-none flex-shrink-0 sm:w-40 tabular-nums -mt-2">
                    {step.num}
                  </span>
                  <div className="flex-1 pt-1">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 rounded-xl bg-[#0a0a0a] flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-xs font-medium text-gray-400 border border-gray-200 px-3 py-1.5 rounded-full">
                        {step.tag}
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-[#0a0a0a] mb-4 tracking-tight">{step.title}</h3>
                    <p className="text-base text-gray-500 leading-relaxed max-w-lg">{step.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════ PROOF MOMENTS ════════ */}
      <section id="proof" className="py-24 sm:py-32 px-5 sm:px-8 bg-[#fafafa] border-y border-black/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-[0.15em] mb-5">In action</p>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <h2 className="text-3xl sm:text-4xl lg:text-[3.25rem] font-bold tracking-tight text-[#0a0a0a] leading-[1.08] max-w-xl">
                This is what planning feels like now.
              </h2>
              <p className="text-sm text-gray-400 max-w-xs leading-relaxed flex-shrink-0">
                Every card is a real WhatsApp notification — from a 5-minute lunch plan to a 5-day trip.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MOMENT_CARDS.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
                className={`border rounded-2xl p-6 sm:p-7 hover:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.1)] hover:scale-[1.01] transition-all duration-300 cursor-default ${card.accent}`}
              >
                <div className="text-4xl mb-5">{card.emoji}</div>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h4 className="text-base font-bold text-[#0a0a0a] leading-snug">{card.title}</h4>
                  <span className="text-[9px] font-semibold text-gray-400 bg-white/70 border border-black/[0.06] px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0 mt-0.5 uppercase tracking-wide">{card.tag}</span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">{card.body}</p>
                <p className="text-[11px] text-gray-400 font-mono">{card.time}</p>
              </motion.div>
            ))}
          </div>

          {/* Plan update — featured full-width card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-5 bg-[#0a0a0a] rounded-2xl p-7 sm:p-9 flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-12"
          >
            <div className="text-4xl flex-shrink-0">🔄</div>

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2.5">
                <h4 className="text-lg font-bold text-white">Changed your mind? Just say so.</h4>
                <span className="text-[9px] font-semibold text-teal-400 bg-teal-400/10 border border-teal-400/20 px-2.5 py-1 rounded-full uppercase tracking-wide flex-shrink-0">Flexible</span>
              </div>
              <p className="text-sm text-white/45 leading-relaxed max-w-md">
                Plans change — that's fine. Shadow rewires everything in one go: hotel, flights, restaurants, transport. You don't chase a single confirmation email.
              </p>
            </div>

            {/* Mini chat exchange */}
            <div className="flex-shrink-0 w-full md:w-80 bg-[#0b141a] rounded-xl border border-white/[0.06] overflow-hidden">
              <div className="px-4 py-2.5 border-b border-white/[0.04] flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <p className="text-[10px] text-white/30 font-medium">Shadow Sidequests</p>
              </div>
              <div className="px-3 py-3 space-y-2">
                {[
                  { from: "user", text: "Actually, can we do Coorg instead of Goa?" },
                  { from: "agent", text: "Done. Flights rerouted to Mysore, hotel switched to The Tamara Coorg — same budget, better reviews. Itinerary updated." },
                ].map((m, i) => (
                  <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[88%] rounded-xl px-3 py-2 text-[11px] leading-relaxed ${
                      m.from === "user"
                        ? "bg-[#005c4b] text-white rounded-tr-sm"
                        : "bg-white/[0.07] text-white/65 rounded-tl-sm"
                    }`}>
                      {m.text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-sm text-gray-400 mt-12"
          >
            Every single one of these — from <span className="text-[#0a0a0a] font-semibold">one WhatsApp chat</span>. No app downloads. No new accounts.
          </motion.p>
        </div>
      </section>

      {/* ════════ INTEGRATIONS + MULTI-PERSON ════════ */}
      <section className="py-24 sm:py-32 px-5 sm:px-8 bg-white border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-[0.15em] mb-5">Built into your life</p>
            <h2 className="text-3xl sm:text-4xl lg:text-[3.25rem] font-bold tracking-tight text-[#0a0a0a] leading-[1.08] max-w-2xl">
              No new apps. No new habits.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            {/* Integrations */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-[#fafafa] border border-black/[0.06] rounded-2xl p-8 sm:p-10 hover:border-black/[0.12] transition-colors"
            >
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-[0.15em] mb-5">Integrations</p>
              <h3 className="text-2xl sm:text-3xl font-bold text-[#0a0a0a] tracking-tight mb-3">
                Stays in sync with
                <br />everything you already use.
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-10 max-w-sm">
                Shadow pushes updates to your calendar, your inbox, and your WhatsApp — wherever you already live.
              </p>

              <div className="space-y-3">
                {[
                  {
                    icon: MessageCircle,
                    name: "WhatsApp",
                    desc: "Your entire trip, all in one chat thread. Bookings, updates, alerts.",
                    bg: "bg-[#25D366]/10",
                    color: "text-[#25D366]",
                  },
                  {
                    icon: Calendar,
                    name: "Google Calendar",
                    desc: "Flights, check-ins, reservations — auto-added with reminders.",
                    bg: "bg-blue-50",
                    color: "text-blue-600",
                  },
                  {
                    icon: Mail,
                    name: "Gmail",
                    desc: "Confirmation emails, itineraries, and receipts sent automatically.",
                    bg: "bg-red-50",
                    color: "text-red-500",
                  },
                ].map((app) => {
                  const AppIcon = app.icon;
                  return (
                    <div key={app.name} className="flex items-start gap-4 bg-white border border-black/[0.05] rounded-xl px-4 py-3.5">
                      <div className={`w-9 h-9 rounded-lg ${app.bg} flex items-center justify-center flex-shrink-0`}>
                        <AppIcon className={`w-4 h-4 ${app.color}`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#0a0a0a] mb-0.5">{app.name}</p>
                        <p className="text-xs text-gray-400 leading-relaxed">{app.desc}</p>
                      </div>
                      <div className="ml-auto flex-shrink-0 mt-0.5">
                        <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">Connected</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Multi-person */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-[#0a0a0a] rounded-2xl p-8 sm:p-10"
            >
              <p className="text-[11px] font-medium text-white/25 uppercase tracking-[0.15em] mb-5">Group trips</p>
              <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-3">
                One booking.
                <br />
                <span className="text-teal-400">Eight people.</span>
              </h3>
              <p className="text-sm text-white/40 leading-relaxed mb-10 max-w-sm">
                Shadow handles group coordination end-to-end — no WhatsApp chaos, no spreadsheets, no one left out.
              </p>

              {/* Avatars */}
              <div className="flex items-center gap-1.5 mb-8">
                {["R", "S", "A", "M", "P", "K", "T", "N"].map((initial, i) => (
                  <div
                    key={i}
                    className="w-9 h-9 rounded-full bg-gradient-to-br flex items-center justify-center text-xs font-bold text-white border-2 border-[#0a0a0a] -ml-1 first:ml-0 flex-shrink-0"
                    style={{
                      background: ["#0d9488","#7c3aed","#db2777","#2563eb","#d97706","#059669","#dc2626","#6d28d9"][i],
                    }}
                  >
                    {initial}
                  </div>
                ))}
                <span className="text-xs text-white/30 ml-2">8 people · Pondicherry</span>
              </div>

              <div className="space-y-2.5">
                {[
                  { icon: Users, text: "Budget voted on WhatsApp — everyone picks their range" },
                  { icon: Wallet, text: "UPI splits tracked automatically, no manual math" },
                  { icon: Calendar, text: "Shared itinerary, everyone gets live updates" },
                  { icon: Check, text: "One person books, everyone's confirmed instantly" },
                ].map((item) => {
                  const ItemIcon = item.icon;
                  return (
                    <div key={item.text} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-lg bg-white/[0.06] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <ItemIcon className="w-3 h-3 text-teal-400" />
                      </div>
                      <p className="text-sm text-white/60 leading-relaxed">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ════════ LIVE TRIP CHAT ════════ */}
      <section className="py-24 sm:py-32 px-5 sm:px-8 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-[11px] font-medium text-teal-400/70 uppercase tracking-[0.15em] mb-5">Live updates</motion.p>
              <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl lg:text-[3.25rem] font-bold tracking-tight text-white leading-[1.08] mb-6">
                It runs itself
                <br />
                while you enjoy.
              </motion.h2>
              <motion.p variants={fadeUp} custom={2} className="text-white/40 text-base leading-relaxed mb-10 max-w-md">
                Delays, weather shifts, running late to your reservation — everything adapted in real time on the same WhatsApp chat. You don't lift a finger.
              </motion.p>
              <motion.div variants={fadeUp} custom={3} className="space-y-5">
                {[
                  { icon: Plane, title: "Flight & Transport", desc: "Delays detected → cab rescheduled, hotel notified, itinerary adjusted." },
                  { icon: CloudRain, title: "Weather Adaptation", desc: "Rain incoming? Indoor plan ready. Beach day moved to tomorrow." },
                  { icon: MapPin, title: "Proximity Alerts", desc: "Hidden waterfall 20 min away? Local market closing? You'll know." },
                ].map((item, i) => {
                  const FIcon = item.icon;
                  return (
                    <motion.div key={item.title} variants={fadeUp} custom={i + 3} className="flex items-start gap-4">
                      <div className="w-9 h-9 rounded-lg border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                        <FIcon className="w-4 h-4 text-white/50" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-white mb-0.5">{item.title}</h4>
                        <p className="text-xs text-white/30 leading-relaxed">{item.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={2}>
              <div className="bg-[#0b141a] rounded-2xl border border-white/[0.06] overflow-hidden shadow-2xl max-w-sm mx-auto">
                <div className="bg-[#1f2c34] px-4 py-3 flex items-center gap-3 border-b border-white/[0.04]">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center">
                    <Navigation className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Shadow Sidequests</p>
                    <p className="text-[10px] text-emerald-400">Live · Goa Day 2</p>
                  </div>
                </div>
                <div className="bg-[#0b141a] px-3 py-4 max-h-[420px] overflow-y-auto">
                  {LIVE_TRIP_MESSAGES.map((msg, i) => (
                    <ChatBubble key={i} message={msg} index={i} />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════ TESTIMONIALS ════════ */}
      <section className="py-24 sm:py-32 px-5 sm:px-8 bg-white border-t border-black/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-[0.15em] mb-5">Real stories</p>
            <h2 className="text-3xl sm:text-4xl lg:text-[3.25rem] font-bold tracking-tight text-[#0a0a0a] leading-[1.08]">
              Real trips. One chat each.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="border border-black/[0.06] rounded-2xl overflow-hidden hover:border-black/[0.14] hover:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.08)] transition-all duration-300"
              >
                <div className="h-1 bg-[#0a0a0a]" />
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-11 h-11 rounded-full ${t.color} flex items-center justify-center text-sm font-bold`}>
                      {t.avatar}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#0a0a0a]">{t.name}, {t.age}</p>
                      <p className="text-xs text-gray-400">{t.city} · {t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">&ldquo;{t.quote}&rdquo;</p>
                  <div className="pt-4 border-t border-black/[0.04] flex items-center gap-2">
                    <MapPin className="w-3 h-3 text-gray-300" />
                    <span className="text-xs font-medium text-gray-400">{t.trip}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ AGENTS ════════ */}
      <section className="py-24 sm:py-32 px-5 sm:px-8 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">

          <div className="mb-16">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-[11px] font-medium text-teal-400/70 uppercase tracking-[0.15em] mb-5"
            >
              The team behind your trip
            </motion.p>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-3xl sm:text-4xl lg:text-[3.25rem] font-bold tracking-tight text-white leading-[1.08] max-w-xl"
              >
                Not one AI.
                <br />
                <span className="text-teal-400">A full team of AI on your trip.</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="text-white/40 text-base leading-relaxed max-w-sm"
              >
                While you're packing, Scout is scanning reels. Booker is confirming your table. Watcher is tracking your flight. Five specialists, one WhatsApp thread — they don't rest until you're there.
              </motion.p>
            </div>
          </div>

          {/* Agent status board */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/[0.04] rounded-2xl overflow-hidden">
            {AGENTS.map((agent, i) => {
              const AgentIcon = agent.icon;
              return (
                <motion.div
                  key={agent.name}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  className="bg-[#0d0d0d] p-6 sm:p-7 flex flex-col gap-5 group hover:bg-[#111] transition-colors"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className={`w-9 h-9 rounded-xl border ${agent.glow} flex items-center justify-center flex-shrink-0`}>
                      <AgentIcon className={`w-4 h-4 ${agent.color}`} />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${agent.dot} animate-pulse`} />
                      <span className="text-[9px] font-semibold text-white/20 uppercase tracking-widest">Active</span>
                    </div>
                  </div>

                  {/* Name + role */}
                  <div>
                    <p className={`text-base font-bold ${agent.color} mb-0.5`}>{agent.name}</p>
                    <p className="text-[10px] font-medium text-white/25 uppercase tracking-wider">{agent.role}</p>
                  </div>

                  {/* Description */}
                  <p className="text-xs text-white/40 leading-relaxed flex-1">{agent.desc}</p>

                  {/* Live status */}
                  <div className={`border ${agent.glow} rounded-lg px-3 py-2`}>
                    <p className="text-[10px] font-mono text-white/30">
                      <span className={`${agent.color} font-semibold`}>→ </span>
                      {agent.status}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Bottom callout */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 px-1"
          >
            <div className="flex items-center gap-2.5">
              <Zap className="w-4 h-4 text-teal-400 flex-shrink-0" />
              <p className="text-sm text-white/30">All five run in parallel, coordinating automatically behind the scenes.</p>
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/[0.08]" />
            <p className="text-sm text-white/30">Everything surfaces in <span className="text-white/60 font-medium">one WhatsApp thread</span> — no new apps, no dashboards.</p>
          </motion.div>

        </div>
      </section>

      {/* ════════ FINAL CTA ════════ */}
      <section className="py-24 sm:py-36 px-5 sm:px-8 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-[11px] font-medium text-white/25 uppercase tracking-[0.15em] mb-8">Start now</p>
            <h2 className="text-4xl sm:text-5xl lg:text-[4.5rem] font-bold tracking-tight text-white leading-[1.02] mb-8">
              Your next sidequest —
              <br />
              big or small —
              <br />
              <span className="text-teal-400">is one message away.</span>
            </h2>
            <p className="text-white/40 text-lg max-w-lg mb-12 leading-relaxed">
              Weekend trip or tonight's dinner. No tabs, no apps, no spreadsheets.
              Just send a message and it comes together.
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-3">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-[#25D366] text-white px-8 py-4 text-sm rounded-lg font-medium flex items-center gap-2.5 hover:bg-[#22c55e] transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                Start on WhatsApp
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <Link
                to="/"
                className="text-white/30 hover:text-white/60 px-6 py-4 text-sm font-medium flex items-center gap-1.5 transition-colors"
              >
                Learn about Shadow
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ FOOTER ════════ */}
      <footer className="py-8 px-5 sm:px-8 border-t border-black/[0.06] bg-white">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-[#0a0a0a] rounded-full" />
            <span className="text-sm font-bold tracking-tight text-[#0a0a0a]">
              SHADOW<span className="text-teal-600">LABS</span>
            </span>
          </div>
          <div className="flex items-center gap-6 text-xs text-gray-400">
            <Link to="/" className="hover:text-[#0a0a0a] transition-colors">Home</Link>
            <Link to="/demo" className="hover:text-[#0a0a0a] transition-colors">Demo</Link>
            <Link to="/privacy" className="hover:text-[#0a0a0a] transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-[#0a0a0a] transition-colors">Terms</Link>
          </div>
          <p className="text-xs text-gray-300">&copy; 2026 ShadowLabs</p>
        </div>
      </footer>
    </div>
  );
}
